/**
 * Gantt Item Component
 *
 * Displays a single roadmap item as a horizontal bar in the Gantt chart.
 */

import { useState } from 'react';

import { VarianceDetailModal } from './VarianceDetailModal';

import type { RoadmapItemWithInvestment } from '@medsuite/models';

interface GanttItemProps {
  item: RoadmapItemWithInvestment;
  left: number;
  width: number;
  extendsBefore: boolean;
  extendsAfter: boolean;
  onClick: () => void;
  stackIndex: number;
}

/**
 * GanttItem Component
 *
 * Features:
 * - Horizontal bar positioned based on start/end dates
 * - Color coding based on commitment status
 * - Top priority indicator
 * - Hover tooltip with details
 */
export function GanttItem({
  item,
  left,
  width,
  extendsBefore,
  extendsAfter,
  onClick,
  stackIndex,
}: GanttItemProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showVarianceModal, setShowVarianceModal] = useState(false);

  // Compute end-date variance in calendar days
  const endVariance = (() => {
    if (!item.baseline_end_date || !item.end_date) {
      return 0;
    }
    const msPerDay = 86400000;
    // Dates may arrive as ISO strings ("2026-03-15T00:00:00.000Z") or date-only ("2026-03-15").
    // Use new Date() directly -- it handles both formats correctly.
    const baseline = new Date(item.baseline_end_date);
    const current = new Date(item.end_date);
    if (isNaN(baseline.getTime()) || isNaN(current.getTime())) {
      return 0;
    }
    return Math.round((current.getTime() - baseline.getTime()) / msPerDay);
  })();

  // Determine colors based on item status
  const getBarClasses = () => {
    const base =
      'absolute h-7 rounded cursor-pointer transition-all duration-150 hover:shadow-md flex items-center justify-center px-2 overflow-hidden';

    if (item.top_priority_flag) {
      // Top priority - orange border
      return `${base} bg-amber-100 dark:bg-amber-900/50 border-2 border-amber-500 dark:border-amber-400`;
    }

    if (item.is_committed) {
      // Committed - green
      return `${base} bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-700`;
    }

    // Default - blue
    return `${base} bg-blue-100 dark:bg-blue-900/50 border border-blue-300 dark:border-blue-700`;
  };

  // Format date for display
  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Calculate vertical offset for stacking
  const top = 4 + stackIndex * 32; // 4px initial padding + 32px per item

  return (
    <div
      className={getBarClasses()}
      style={{
        left: `${left}%`,
        width: `${Math.max(width, 2)}%`,
        top: `${top}px`,
      }}
      onClick={onClick}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Extends Before Indicator */}
      {extendsBefore && <span className="text-gray-500 dark:text-gray-400 mr-1 text-xs">...</span>}

      {/* Item Label */}
      <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">
        {item.investment_title || `Item #${item.roadmap_item_id}`}
      </span>

      {/* Priority Flag */}
      {item.top_priority_flag && (
        <svg
          className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 ml-1"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z"
            clipRule="evenodd"
          />
        </svg>
      )}

      {/* Committed Indicator */}
      {item.is_committed && !item.top_priority_flag && (
        <svg
          className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0 ml-1"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}

      {/* Variance Badge */}
      {endVariance !== 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowVarianceModal(true);
          }}
          className={`flex-shrink-0 ml-1 px-1 rounded text-[10px] font-bold leading-tight ${
            endVariance > 0
              ? 'bg-red-200 dark:bg-red-800/60 text-red-700 dark:text-red-300'
              : 'bg-green-200 dark:bg-green-800/60 text-green-700 dark:text-green-300'
          }`}
        >
          {endVariance > 0 ? `+${endVariance}d` : `${endVariance}d`}
        </button>
      )}

      {/* Extends After Indicator */}
      {extendsAfter && <span className="text-gray-500 dark:text-gray-400 ml-1 text-xs">...</span>}

      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute z-50 bottom-full left-0 mb-2 w-64 rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-700 p-3 pointer-events-none"
          style={{ minWidth: '240px' }}
        >
          <div className="font-medium text-gray-900 dark:text-gray-100 text-sm">
            {item.investment_title || `Item #${item.roadmap_item_id}`}
          </div>

          <div className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
            <div className="flex justify-between">
              <span>Start:</span>
              <span className="text-gray-900 dark:text-gray-200">
                {formatDate(item.start_date)}
              </span>
            </div>
            <div className="flex justify-between">
              <span>End:</span>
              <span className="text-gray-900 dark:text-gray-200">{formatDate(item.end_date)}</span>
            </div>
            {item.is_committed && (
              <div className="flex justify-between">
                <span>Committed:</span>
                <span className="text-green-600 dark:text-green-400">Yes</span>
              </div>
            )}
            {item.delivery_date && (
              <div className="flex justify-between">
                <span>Delivery:</span>
                <span className="text-gray-900 dark:text-gray-200">
                  {formatDate(item.delivery_date)}
                </span>
              </div>
            )}
            {item.estimated_effort_days > 0 && (
              <div className="flex justify-between">
                <span>Effort:</span>
                <span className="text-gray-900 dark:text-gray-200">
                  {item.estimated_effort_days} days
                </span>
              </div>
            )}
            {item.estimated_remaining_days > 0 && (
              <div className="flex justify-between">
                <span>Remaining:</span>
                <span className="text-gray-900 dark:text-gray-200">
                  {item.estimated_remaining_days} days
                </span>
              </div>
            )}
          </div>

          {/* Baseline info in tooltip */}
          {item.baseline_end_date && (
            <>
              <div className="flex justify-between">
                <span>Baseline End:</span>
                <span className="text-gray-900 dark:text-gray-200">
                  {formatDate(item.baseline_end_date)}
                </span>
              </div>
              {endVariance !== 0 && (
                <div className="flex justify-between">
                  <span>Variance:</span>
                  <span
                    className={
                      endVariance > 0
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-green-600 dark:text-green-400'
                    }
                  >
                    {endVariance > 0 ? `+${endVariance}` : endVariance} days
                  </span>
                </div>
              )}
            </>
          )}

          {/* Status Badges */}
          <div className="mt-2 flex flex-wrap gap-1">
            {item.is_committed && (
              <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900/50 px-2 py-0.5 text-xs font-medium text-green-800 dark:text-green-200">
                Committed
              </span>
            )}
            {item.top_priority_flag && (
              <span className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-200">
                Priority
              </span>
            )}
          </div>
        </div>
      )}

      {/* Variance Detail Modal */}
      {showVarianceModal && (
        <VarianceDetailModal item={item} onClose={() => setShowVarianceModal(false)} />
      )}
    </div>
  );
}

export default GanttItem;
